// src/modules/myvault/components/MyVaultAgent/ChatPanel.tsx
//
// Chat panel component using mmagent-chat-ui for the MyVault Agent.

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Typography,
  IconButton,
  Input,
  CircularProgress,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Divider,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from "@mui/joy";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import HistoryIcon from "@mui/icons-material/History";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  ChatProvider,
  useChat,
  useConversations,
  useLoadConversation,
  useChatStore,
  ChatMessage,
  useConfirmation,
  SkillConfirmationModal,
  AttachmentPicker,
} from "@metalmind-ab/mmagent-chat-ui";
import type {
  ChatMessageType,
  PendingConfirmation,
  SelectedImage,
  SelectedAttachment,
} from "@metalmind-ab/mmagent-chat-ui";
import "@metalmind-ab/mmagent-chat-ui/styles.css";

import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";
import { getApiBaseUrl } from "../../../../config/runtimeConfig";
import { buildChatResumeOptions } from "../../../../utils/chatResumeOptions";
import { settleMyVaultSend } from "./chatStreamFinalization";
import { useChatImageAttachments } from "../../../../hooks/useChatImageAttachments";
import { mapConversationMessages } from "../../../ida/utils/chatMessageMapper";
import { emitMyVaultToolChange } from "../../services/workspaceObjectChanges";

interface ChatPanelProps {
  workspaceId: string;
  canvasId?: string;
}

// The chat package keeps its active conversation in a module-level store. Track
// the scope across panel unmounts so closing and reopening the same panel keeps
// its conversation, while switching between workspace and canvas assistants
// clears the shared store before another namespace is shown.
let activeMyVaultChatScope: string | null = null;

/**
 * Chat panel component that provides the MyVault agent chat interface.
 */
export const ChatPanel: React.FC<ChatPanelProps> = ({
  workspaceId,
  canvasId,
}) => {
  const [authHeaders, setAuthHeaders] = useState<Record<string, string> | null>(
    null,
  );
  const [authError, setAuthError] = useState<string | null>(null);
  // Track the last workspaceId this mount has seen so we can clear shared
  // chat state when the user switches workspaces while the component is
  // mounted. Local state (not localStorage) because localStorage is shared
  // across tabs -- seeding from it caused the first render in a new tab to
  // initialize against whichever workspace any other tab last touched.
  const [currentChatScope, setCurrentChatScope] = useState<string | null>(null);
  const chatScope = `${workspaceId}:${canvasId ? `canvas:${canvasId}` : "workspace"}`;
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;
  const sendGenerationRef = useRef(0);
  const contextVersionRef = useRef(0);
  const getAuthHeaders = useAuthHeaders();
  const apiBaseUrl = `${getApiBaseUrl()}/api/workspace/myvault`;
  const canvasHeaders = useMemo<Record<string, string>>(() => {
    const headers: Record<string, string> = {};
    if (canvasId) headers["X-MyVault-Canvas-Id"] = canvasId;
    return headers;
  }, [canvasId]);
  const imageAttachments = useChatImageAttachments(
    apiBaseUrl,
    workspaceId,
    getAuthHeaders,
    canvasHeaders,
  );

  // Get store actions to reset state when context changes
  const clearMessages = useChatStore((state) => state.clearMessages);
  const setConversationId = useChatStore((state) => state.setConversationId);
  const setConversations = useChatStore((state) => state.setConversations);
  const setLoading = useChatStore((state) => state.setLoading);

  // The host chat store is shared by workspace and canvas panels, while the
  // API keeps their conversations in separate namespaces. Clear the store
  // when switching namespace so no previous conversation is shown or reused.
  useEffect(() => {
    if (!workspaceId) return;

    if (activeMyVaultChatScope !== chatScope) {
      contextVersionRef.current += 1;
      sendGenerationRef.current += 1;
      clearMessages();
      setConversationId(null);
      setConversations([], 0);
      activeMyVaultChatScope = chatScope;
    }

    setCurrentChatScope(chatScope);
    setLoading(false);
  }, [
    workspaceId,
    chatScope,
    clearMessages,
    setConversationId,
    setConversations,
    setLoading,
  ]);

  // Fetch auth headers on mount
  useEffect(() => {
    const fetchHeaders = async () => {
      try {
        const headers = await getAuthHeaders();
        setAuthHeaders((previous) => {
          if (
            previous &&
            Object.keys(previous).length === Object.keys(headers).length &&
            Object.entries(headers).every(
              ([key, value]) => previous[key] === value,
            )
          ) {
            return previous;
          }
          return headers;
        });
      } catch (err) {
        setAuthError((err as Error).message);
      }
    };
    fetchHeaders();
  }, [getAuthHeaders]);

  // Ref to bridge confirmation callback from ChatContent to ChatProvider config
  // Must be declared before early returns to satisfy React hooks rules
  const confirmationHandlerRef = useRef<
    ((confirmation: PendingConfirmation) => void) | null
  >(null);

  const handleConfirmationRequired = useCallback(
    (confirmation: PendingConfirmation) => {
      confirmationHandlerRef.current?.(confirmation);
    },
    [],
  );
  const handleToolEnd = useCallback(
    (toolName: string, args: Record<string, unknown>, result: unknown) => {
      emitMyVaultToolChange(workspaceId, toolName, args, result);
    },
    [workspaceId],
  );

  const headers: Record<string, string> = useMemo(
    () => ({ ...authHeaders, "X-Workspace-Id": workspaceId, ...canvasHeaders }),
    [authHeaders, canvasHeaders, workspaceId],
  );
  const uploadAttachment = imageAttachments.uploadImage;
  const uploadImage = useCallback(
    (file: File) =>
      uploadAttachment(file, useChatStore.getState().conversationId),
    [uploadAttachment],
  );
  const config = useMemo(
    () => ({
      apiBaseUrl,
      headers,
      imageAttachmentsEnabled: imageAttachments.enabled,
      uploadImage,
      onConfirmationRequired: handleConfirmationRequired,
      onToolEnd: handleToolEnd,
      // The MyVault API owns the run, so Stop must cancel it there and wait for confirmation
      // (POST .../chat/conversations/{id}/active-submission/cancel).
      cancelSubmission: true as const,
      // Task #580 Phase 3b: SSE Last-Event-ID resume against BE broker.
      ...buildChatResumeOptions(apiBaseUrl),
    }),
    [
      apiBaseUrl,
      headers,
      imageAttachments.enabled,
      uploadImage,
      handleConfirmationRequired,
      handleToolEnd,
    ],
  );

  // Wait for auth headers
  if (authHeaders === null && !authError) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (authError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography level="body-sm" color="danger">
          Authentication failed: {authError}
        </Typography>
      </Box>
    );
  }

  // Avoid briefly rendering a conversation from another MyVault namespace
  // while the shared chat store is being switched to this panel's scope.
  if (currentChatScope !== chatScope) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size="sm" />
      </Box>
    );
  }

  return (
    <ChatProvider key={chatScope} config={config}>
      <ChatContent
        apiBaseUrl={apiBaseUrl}
        headers={headers}
        imageAttachments={imageAttachments}
        confirmationHandlerRef={confirmationHandlerRef}
        workspaceIdRef={workspaceIdRef}
        sendGenerationRef={sendGenerationRef}
        contextVersionRef={contextVersionRef}
      />
    </ChatProvider>
  );
};

interface ChatContentProps {
  apiBaseUrl: string;
  headers: Record<string, string>;
  imageAttachments: ReturnType<typeof useChatImageAttachments>;
  confirmationHandlerRef: React.MutableRefObject<
    ((confirmation: PendingConfirmation) => void) | null
  >;
  workspaceIdRef: React.MutableRefObject<string>;
  sendGenerationRef: React.MutableRefObject<number>;
  contextVersionRef: React.MutableRefObject<number>;
}

const ChatContent: React.FC<ChatContentProps> = ({
  apiBaseUrl,
  headers,
  imageAttachments,
  confirmationHandlerRef,
  workspaceIdRef,
  sendGenerationRef,
  contextVersionRef,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [images, setImages] = useState<SelectedImage[]>([]);
  const [imageSendError, setImageSendError] = useState<string | null>(null);
  const [imageAttemptCount, setImageAttemptCount] = useState<number | null>(
    null,
  );
  const attachments: SelectedAttachment[] = images.map((image) => ({
    ...image,
    kind: "image",
  }));
  const handleAttachmentsChange = useCallback((next: SelectedAttachment[]) => {
    setImages(
      next.flatMap((item) =>
        item.kind === "image"
          ? [
              {
                reference: item.reference,
                fileName: item.fileName,
                previewUrl: item.previewUrl,
              },
            ]
          : [],
      ),
    );
    setImageSendError(null);
    setImageAttemptCount(null);
  }, []);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Conversation history state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredConvoId, setHoveredConvoId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // mmagent-chat-ui hooks
  // Note: sendConfirmationResponse was removed from useChat() in v0.1.68 — the library
  // now streams confirmation responses internally via useConfirmation. streamHandler was
  // likewise removed from UseConfirmationOptions for the same reason.
  const {
    messages,
    sendMessage,
    isLoading,
    cancelStream,
    isStopping,
    stopError,
  } = useChat();
  const latestMessage = messages[messages.length - 1];
  const serverImageRefusal =
    imageAttemptCount !== null &&
    messages.length > imageAttemptCount &&
    latestMessage?.role === "assistant" &&
    (latestMessage.errorCode === "IMAGE_INPUT_UNSUPPORTED" ||
      latestMessage.errorCode === "IMAGE_INPUT_DISABLED")
      ? latestMessage.content
      : null;
  const imageRefusal = imageSendError || serverImageRefusal;
  const conversationId = useChatStore((state) => state.conversationId);

  // An unsent selection belongs to the conversation it was made in. The
  // picker releases the preview URLs when the controlled selection changes,
  // so the host only has to empty it.
  const previousConversationIdRef = useRef(conversationId);
  useEffect(() => {
    if (previousConversationIdRef.current === conversationId) return;
    previousConversationIdRef.current = conversationId;
    setImages([]);
    setImageSendError(null);
    setImageAttemptCount(null);
  }, [conversationId]);

  // Skill confirmation handling — response streaming is handled internally by useConfirmation
  const { activeConfirmation, approve, reject, handleConfirmation } =
    useConfirmation({
      apiBaseUrl,
      headers,
      conversationId: conversationId ?? undefined,
    });

  // Register handleConfirmation so ChatProvider's onConfirmationRequired can call it
  useEffect(() => {
    confirmationHandlerRef.current = handleConfirmation;
    return () => {
      confirmationHandlerRef.current = null;
    };
  }, [handleConfirmation, confirmationHandlerRef]);
  const {
    conversations,
    search,
    loadMore,
    loading: conversationsLoading,
    deleteConversation,
    hasMore,
  } = useConversations();
  const setConversationId = useChatStore((state) => state.setConversationId);
  const { startNewConversation } = useLoadConversation();
  const loadMyVaultConversation = useCallback(
    async (id: number) => {
      const response = await fetch(
        `${apiBaseUrl}/chat/conversations/${id}/messages`,
        { headers },
      );
      if (!response.ok) {
        throw new Error(`Failed to load conversation: ${response.status}`);
      }

      const payload = (await response.json()) as {
        items?: Parameters<typeof mapConversationMessages>[0];
      };
      const mappedMessages = mapConversationMessages(payload.items ?? []);
      useChatStore.getState().setMessages(mappedMessages);
      setConversationId(id);
      return mappedMessages;
    },
    [apiBaseUrl, headers, setConversationId],
  );

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when loading completes
  const prevIsLoading = useRef(isLoading);
  useEffect(() => {
    if (prevIsLoading.current && !isLoading) {
      inputRef.current?.focus();
    }
    prevIsLoading.current = isLoading;
  }, [isLoading]);

  // Global ESC key listener to cancel stream
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isLoading && !isStopping && cancelStream) {
        e.preventDefault();
        cancelStream();
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [isLoading, isStopping, cancelStream]);

  const handleSend = useCallback(async () => {
    if ((!inputValue.trim() && images.length === 0) || isLoading) return;

    const generation = ++sendGenerationRef.current;
    const contextVersion = contextVersionRef.current;
    const sendWorkspaceId = workspaceIdRef.current;
    const store = useChatStore.getState();
    const previousMessageIds = new Set(
      store.messages.map((message) => message.id),
    );
    let assistantMessageCreatedAt: Date | null = null;

    const message = inputValue.trim();
    const clear = () => {
      setImageAttemptCount(null);
      setInputValue("");
      setImages([]);
    };
    setImageSendError(null);
    setImageAttemptCount(images.length > 0 ? messages.length : null);
    if (images.length === 0) clear();
    let sendFailed = false;
    try {
      const sendPromise = sendMessage(
        message,
        undefined,
        images.map((image) => image.reference),
        images.length > 0 ? clear : undefined,
      );
      // The package remaps the assistant row's temporary ID to the server ID
      // from the done frame. Its creation timestamp object survives that
      // update, so use it as this send's stable row identity.
      assistantMessageCreatedAt =
        [...useChatStore.getState().messages]
          .reverse()
          .find(
            (candidate) =>
              candidate.role === "assistant" &&
              !previousMessageIds.has(candidate.id),
          )?.createdAt ?? null;
      await sendPromise;
    } catch (error) {
      sendFailed = true;
      setImageSendError(
        error instanceof Error ? error.message : "Unable to send message",
      );
    } finally {
      // The pinned chat package stops reading after the first `done` frame.
      // If a tool_end follows that frame, its chip stays running. Settle only
      // this send's assistant message when its promise has ended.
      const currentStore = useChatStore.getState();
      const current = {
        generation: sendGenerationRef.current,
        contextVersion: contextVersionRef.current,
        workspaceId: workspaceIdRef.current,
      };
      const ownedMessage = currentStore.messages.find(
        (candidate) =>
          candidate.role === "assistant" &&
          candidate.createdAt === assistantMessageCreatedAt,
      );
      const settlement = settleMyVaultSend(
        currentStore.messages,
        {
          generation,
          contextVersion,
          workspaceId: sendWorkspaceId,
          assistantMessageCreatedAt,
        },
        current,
        sendFailed || ownedMessage?.isError ? "failed" : "completed",
      );
      if (settlement.messages !== currentStore.messages) {
        const finalized = settlement.messages.find(
          (candidate) =>
            candidate.role === "assistant" &&
            candidate.createdAt === assistantMessageCreatedAt,
        );
        if (finalized) {
          currentStore.updateMessage(finalized.id, {
            isStreaming: finalized.isStreaming,
            isWorking: finalized.isWorking,
            toolExecutions: finalized.toolExecutions,
          });
        }
      }
      if (settlement.clearLoading) {
        currentStore.setLoading(false);
      }
    }
  }, [
    inputValue,
    images,
    isLoading,
    sendMessage,
    messages.length,
    workspaceIdRef,
    sendGenerationRef,
    contextVersionRef,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Conversation history handlers
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      search(query);
    },
    [search],
  );

  const handleNewConversation = useCallback(() => {
    contextVersionRef.current += 1;
    startNewConversation();
    setImages([]);
    setImageSendError(null);
    setImageAttemptCount(null);
    setHistoryOpen(false);
  }, [startNewConversation, contextVersionRef]);

  const handleLoadConversation = useCallback(
    (id: number) => {
      contextVersionRef.current += 1;
      loadMyVaultConversation(id);
      setHistoryOpen(false);
    },
    [loadMyVaultConversation, contextVersionRef],
  );

  const handleDeleteConversation = useCallback(
    (id: number) => {
      deleteConversation(id);
      setDeleteConfirmId(null);
    },
    [deleteConversation],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.level1",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography level="title-sm" noWrap>
              MyVault Assistant
            </Typography>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }} noWrap>
              Database, Views & Canvas Management
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Conversation history">
              <IconButton
                variant="plain"
                color="neutral"
                size="sm"
                onClick={() => setHistoryOpen(true)}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="New conversation">
              <IconButton
                variant="plain"
                color="primary"
                size="sm"
                onClick={handleNewConversation}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {messages.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              color: "text.tertiary",
              py: 4,
            }}
          >
            <Typography level="body-sm">
              I can help you manage your database schema, views, and canvas
              applications.
            </Typography>
            <Typography level="body-xs" sx={{ mt: 1 }}>
              Ask me to create tables, add columns, design views, or build
              canvases.
            </Typography>
          </Box>
        ) : (
          messages.map((message: ChatMessageType) => (
            <ChatMessage key={message.id} message={message} />
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.level1",
        }}
      >
        {stopError && (
          <Typography color="danger" level="body-sm" role="alert">
            {stopError}
          </Typography>
        )}
        {imageRefusal &&
          !serverImageRefusal &&
          imageSendError !== "IMAGE_INPUT_UNSUPPORTED" &&
          imageSendError !== "IMAGE_INPUT_DISABLED" && (
            <Typography color="danger" level="body-sm">
              {imageRefusal}
            </Typography>
          )}
        {/* The picker renders its button, chips and errors as one unit. The
            display:contents rules make the button a flex item of this row (left
            of the input) and let chips/errors wrap onto their own line above. */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
            "& .mmagent-attachments": { display: "contents" },
            "& .mmagent-attachments__chips": { order: -1, flex: "0 0 100%" },
            "& .mmagent-attachments__chips:empty": { display: "none" },
            "& .mmagent-attachments__error": { order: -1, flex: "0 0 100%" },
          }}
        >
          {imageAttachments.enabled && (
            <AttachmentPicker
              attachments={attachments}
              onChange={handleAttachmentsChange}
              uploadImage={(file) =>
                imageAttachments.uploadImage(
                  file,
                  useChatStore.getState().conversationId,
                )
              }
              disabled={isLoading}
              pasteTargetRef={inputRef}
              refusal={
                serverImageRefusal ||
                (imageSendError === "IMAGE_INPUT_UNSUPPORTED" ||
                imageSendError === "IMAGE_INPUT_DISABLED"
                  ? imageSendError
                  : null)
              }
            />
          )}
          <Input
            ref={inputRef}
            placeholder="Ask about database, views, canvases..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            sx={{ flex: 1 }}
            size="sm"
          />
          {isLoading ? (
            <Tooltip
              title={isStopping ? "Stopping..." : "Stop generating (ESC)"}
            >
              <IconButton
                onClick={cancelStream}
                disabled={isStopping}
                color="danger"
                variant="soft"
                size="sm"
              >
                <StopIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              onClick={handleSend}
              disabled={!inputValue.trim() && images.length === 0}
              color="primary"
              variant="solid"
              size="sm"
            >
              <SendIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Conversation history drawer */}
      <Drawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        anchor="left"
        size="sm"
      >
        <Box sx={{ p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography level="title-lg">Conversations</Typography>
            <IconButton
              variant="plain"
              color="neutral"
              onClick={() => setHistoryOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          <Input
            startDecorator={<SearchIcon />}
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            size="sm"
          />
        </Box>
        <Divider />
        <List sx={{ py: 0 }}>
          <ListItem>
            <ListItemButton onClick={handleNewConversation}>
              <AddIcon sx={{ mr: 1 }} />
              <ListItemContent>
                <Typography level="body-sm">New conversation</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>
          <Divider />
          {conversationsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : conversations.length === 0 ? (
            <ListItem>
              <ListItemContent>
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                  No conversations yet
                </Typography>
              </ListItemContent>
            </ListItem>
          ) : (
            <>
              {conversations.map((convo) => (
                <ListItem
                  key={convo.id}
                  onMouseEnter={() => setHoveredConvoId(convo.id)}
                  onMouseLeave={() => setHoveredConvoId(null)}
                  endAction={
                    hoveredConvoId === convo.id ? (
                      <IconButton
                        aria-label="Delete"
                        size="sm"
                        color="danger"
                        variant="plain"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(convo.id);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    ) : undefined
                  }
                >
                  <ListItemButton
                    selected={convo.id === conversationId}
                    onClick={() => handleLoadConversation(convo.id)}
                  >
                    <ListItemContent>
                      <Typography level="body-sm">
                        {convo.title || `Conversation ${convo.id}`}
                      </Typography>
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.tertiary" }}
                      >
                        {new Date(convo.createdAt).toLocaleDateString()}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ))}
              {hasMore && (
                <ListItem>
                  <ListItemButton onClick={loadMore}>
                    <ListItemContent>
                      <Typography level="body-sm" sx={{ color: "primary.500" }}>
                        Load older...
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              )}
            </>
          )}
        </List>
      </Drawer>

      {/* Delete confirmation modal */}
      <Modal
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>Delete conversation?</DialogTitle>
          <DialogContent>
            This will permanently delete this conversation and all its messages.
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="danger"
              onClick={() => {
                if (deleteConfirmId) {
                  handleDeleteConversation(deleteConfirmId);
                }
              }}
            >
              Delete
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteConfirmId(null)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Skill confirmation modal (destructive operations) */}
      {activeConfirmation && (
        <SkillConfirmationModal
          confirmation={activeConfirmation}
          onApprove={() => approve(activeConfirmation.confirmationId)}
          onReject={() => reject(activeConfirmation.confirmationId)}
        />
      )}
    </Box>
  );
};

export default ChatPanel;
